import exportFromJSON from "export-from-json";
import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { getAgeementMatchDataList } from "../services/agreementMatchDataService";
import { Typography } from "@mui/material";

// One place per tab, sharing a base rather than three near-copies. The copies are how the
// default list ended up asking for "fullName" -- a column vwagreementmatchdata does not have, so
// it exported empty for every row until this branch caught it.
const BASE_FIELDS = [
  "dealerId",
  "agreementId",
  "productCode",
  "isSentToST",
  "tenantId",
  "locationId",
  "customerName",
  "fullAddress",
  "phoneNumber",
  "email",
  "purchaseDate",
  "startDate",
  "expirationDate",
  "pdfURL",
];

// The All tab's own columns bracket the shared ones: the two identifiers lead, then what this tab
// exists to show, then the base columns, then the two flags saying why a row was included, with
// pdfURL last as it is on every tab.
//
// Selected BY NAME, not by position. This was BASE_FIELDS.slice(2, 12), which was correct only
// while BASE_FIELDS had exactly its original thirteen entries in that order: US 330 (#30) then
// inserted "tenantId" straight after "isSentToST", which would have shifted expirationDate out of
// the slice and silently dropped Expiration Date from the All-tab CSV. Nothing would have failed.
// The name-based filter survives any insertion, and is what carries tenantId into the All-tab
// export.
const ALL_TAB_LEADING = ["dealerId", "agreementId"];
const ALL_TAB_TRAILING = ["pdfURL"];

// One list per tab. These SELECT columns; they do not order them -- export-from-json treats
// `fields` as a filter and emits the CSV in the data's own key order, which an e2e assertion on
// the All-tab header confirmed. Kept written in reading order anyway, as documentation of intent.
const FIELDS_BY_TAB = {
  default: BASE_FIELDS,
  all: [
    ...ALL_TAB_LEADING,
    "origin",
    "syncState",
    "status",
    ...BASE_FIELDS.filter(
      (field) => !ALL_TAB_LEADING.includes(field) && !ALL_TAB_TRAILING.includes(field),
    ),
    "isExpired",
    "isCancelled",
    ...ALL_TAB_TRAILING,
  ],
  // The Map Location tab shows the JBW side of an unmatched agreement, so it swaps the matched
  // columns for their jbw* counterparts.
  nolocation: [
    "dealerId",
    "agreementId",
    "jbwProductCode",
    "isSentToST",
    "tenantId",
    "locationId",
    "jbwCustomerName",
    "jbwFullAddress",
    "jbwPhoneNumber",
    "jbwEmail",
    "jbwPurchaseDate",
    "startDate",
    "expirationDate",
    "pdfURL",
  ],
};

const AgreementSyncDataDownload = ({
  datatype,
  dealerId,
  openDownload,
  onClose,
  dealerName,
  userName,
  tabType,
  includeExpiredCancelled = false,
  searchAgreementId,
  quickFilter = "",
}) => {
  console.log("AgreementSyncDownlaod", openDownload);

  const [open, setOpen] = useState(openDownload);
  const handleOpen = () => setOpen(true);
  const handleClose = (reason) => {
    if (reason === "escapeKeyDown" || reason === "backdropClick") {
      return;
    }
    onClose();
  }
  const [jsonData, setJsonData] = useState(true);

  const fetchData = () => {
    if (!openDownload) return;
    console.log("fetching download data");
    // limit=-1 asks the handler to drop the LIMIT clause and return the whole tab in one query,
    // which is what all four tabs do. The All tab's union is heavier than the match view, so a
    // batched version of this was written and then removed: it multiplied the handler's
    // per-invocation connections and its unbounded COUNT by one per page, and nothing tested it.
    // Paging the export is worth revisiting now that each arm binds its own dealerId.
    //
    // An agreement-id search is exported as the search result, not the whole tab: the grid is
    // showing that one agreement, and #319's answer is that the download respects what is shown.
    // The All tab's quick filter rides along for the same reason; the handler applies it to the
    // export exactly as it does to the page.
    getAgeementMatchDataList(dealerId, {
      datatype: searchAgreementId ? "searchAgreement" : datatype,
      page: 0, limit: -1, tabType, includeExpiredCancelled, searchAgreementId, quickFilter,
    })
      .then((res) => res.data.data)
      .then((rows) => {
        setJsonData(rows);
        if (rows && rows.length > 0) {
          createDownloadFile(rows);
        } else {
          setJsonData(false);
          setTimeout(() => { handleClose(); }, 2000)
        }
      })
      .catch((err) => {
        console.error("Internal server error : ", err);
        // Without this the modal sits open forever: it sets disableEscapeKeyDown and ignores
        // backdrop clicks, so a failed fetch used to mean reloading the page.
        setJsonData(false);
        setTimeout(() => { handleClose(); }, 2000)
      });
  };

  // One place for the tab -> label mapping. The filename and the dialog title used to carry
  // separate copies of the same nested ternary, which is how "all" would have ended up
  // labelled "NoLocations".
  const tabLabel = (labels) => labels[tabType] ?? labels.nolocation;

  const createDownloadFile = (data) => {
    console.log("createDownloadFile", data.length);
    const fileName = `${dealerId}-${userName}-agreement-sync-export-${tabLabel({ approved: "Approved", unapproved: "UnApproved", all: "All", nolocation: "NoLocations" })}-${new Date().getTime()}`;
    const exportType = exportFromJSON.types.csv;
    let fields;
    fields = FIELDS_BY_TAB[tabType] ?? FIELDS_BY_TAB.default;

    exportFromJSON({ data, fileName, fields, exportType });

    handleClose();
  };

  // This component is mounted on click and fetches from a bare-deps effect, so React 18's
  // StrictMode double-invocation ran it twice and produced two identical CSVs with different
  // timestamps in the filename (recorded on ADO 276). The ref makes the fetch idempotent.
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    console.log("calling fetch");
    fetchData();

    // if (openDownload) {
    //   fetchData();
    // }
  }, []);

  //   useEffect(()=>{
  //     console.log("jsondata",jsonData)
  //     if(jsonData){
  //         createDownloadFile(jsonData)
  //     }

  //   },[jsonData.length>0])

  return (
    <Dialog
      open={openDownload}
      onClose={handleClose}
      disableEscapeKeyDown
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <Typography variant="subtitle1">
          Downloading {tabLabel({ approved: "Approved", unapproved: "Un Approved", all: "All", nolocation: "No Locations" })} Data
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Typography variant="caption" display="block">
            {!jsonData ? "Sorry No Data Found Closing Dialog" : ` Please wait Download data for ${dealerName} is in progress......`}
          </Typography>

        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default AgreementSyncDataDownload;
